import { Autocomplete, Divider, Grid, TextField, Typography } from '@mui/material'
import React, { useContext, useEffect, useRef } from 'react'
import globalStyles from "../../../../../../assets/styles/global.module.scss";
import popupStyles from "../../../../../../assets/styles/componentStyles/globalPopupStyles.module.scss"
import workflowStyles from "../../../../../../assets/styles/componentStyles/manychatStyles/workflowFormComponents/workflowGlobal.module.scss"
import styles from '../../../../../../assets/styles/componentStyles/manychatStyles/workflowFormComponents/workflowForm.module.scss'
import contactStyles from './../../../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss'
import GlobalButton from '../../../../../utility/globalButton/button';
import { ContactContext, ContactContextPayload } from '../../../../../../context/contactContext';
import { UserContext, UserContextPayload } from '../../../../../../context/userContext';
import { emptyContactListObj } from '../../../flowBuilderConstants';
import Required from '../../../../../utility/required';
import { filterAutocompleteOptionsWithException } from '../../../../../../utils/constants';
import { campaignContactFieldData, getCampaignContactListResponse } from '../../../../../../models/axiosModel';
import { isCurrentViewVersionPublished } from '../../../utilityFunctions';
import { WorkflowContext, WorkflowContextPayload } from '../../../../../../context/workflowContext';
import RenderCustomOption from '../../../../../global/autocomplete/renderCustomOption';
import { useNavigate } from 'react-router-dom';
import { FlowBuilderContext, FlowBuilderContextPayload } from '../../../../../../context/flowBuilderContext';
import GlobalTooltip from '../../../../../utility/tooltip/globalTooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import RetrieveContactVariableConfig from './retrieveContactVariableConfig';
import { retrieveContactActionData, retrieveContactActionListData, retrieveContactActionVariableData } from '../../../../../../models/flowBuilderTypes';
import { filterOutArchivedContactLists } from '../../../../contacts/contactConstant';

interface RetrieveContactPopupProps {
    actionData: retrieveContactActionData, 
    stepAction: any, 
    setFlagRetrieveContactPopup: React.Dispatch<React.SetStateAction<boolean>>,
    handleCancelClick: () => void,
} 

const RetrieveContactPopup: React.FC<RetrieveContactPopupProps> = (props) => {
    
    const { actionData, stepAction, setFlagRetrieveContactPopup, handleCancelClick } = props;

    const { campaignContactListData, getCampaignContactListApi, setFlagAddNewListDialog, getListOfCampaignContactFieldApi } = useContext(ContactContext) as ContactContextPayload;
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { currentActiveStaticWorkflow } = useContext(WorkflowContext) as WorkflowContextPayload
    const { onChangeCurrentActiveNodeConfig, nodes, onBlurOfFallBackOfBtnVar, currentActiveNodeConfig } = useContext(FlowBuilderContext) as FlowBuilderContextPayload
    
    const navigate = useNavigate();

    const fetchedContactListIdsRef = useRef<Set<number>>(new Set());
    const pendingAddedListIdsRef = useRef<number[]>([]);
    const hasFetchedExistingListsRef = useRef(false);
    const contactListFieldsMapRef = useRef<Map<number, campaignContactFieldData[]>>(new Map());
    const syncedVariableFieldNamesRef = useRef<Set<string>>(new Set());
    const retrieveContactLoaderCountRef = useRef(0);

    const startRetrieveContactLoader = () => {
        retrieveContactLoaderCountRef.current += 1;
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
    };

    const stopRetrieveContactLoader = () => {
        retrieveContactLoaderCountRef.current = Math.max(0, retrieveContactLoaderCountRef.current - 1);
        if (retrieveContactLoaderCountRef.current === 0) {
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        }
    };

    useEffect(() => {
        syncedVariableFieldNamesRef.current = new Set(
            (actionData?.action_retrieve_contact_variable_data ?? [])?.map((variable: retrieveContactActionVariableData) => variable?.contact_field_name)
        );
    }, [actionData?.action_retrieve_contact_variable_data]);

    const getContactFieldNamesToRemove = (removedListIds: number[], remainingListIds: number[]) => {
        if (remainingListIds.length === 0) {
            return [];
        }

        const remainingFieldNames = new Set<string>();
        remainingListIds.forEach((listId) => {
            const fields = contactListFieldsMapRef.current.get(listId) ?? [];
            fields.forEach((field) => remainingFieldNames.add(field.contact_field_name));
        });

        const fieldNamesToRemove = new Set<string>();
        removedListIds.forEach((listId) => {
            const fields = contactListFieldsMapRef.current.get(listId) ?? [];
            fields.forEach((field) => {
                if (!field.flag_default && !remainingFieldNames.has(field.contact_field_name)) {
                    fieldNamesToRemove.add(field.contact_field_name);
                }
            });
        });

        return Array.from(fieldNamesToRemove);
    };

    const getContactListApi = async () => {
        startRetrieveContactLoader();
        try {
            const res = await getCampaignContactListApi();
            if (!(res.status)) {
                handleError(res);
            }
        } finally {
            stopRetrieveContactLoader();
        }
    }

    useEffect(() => {
        if(!campaignContactListData){
            getContactListApi();
        }
    }, [])

    const storeFieldsFromResponse = (contactListId: number, contactFieldData: campaignContactFieldData[]) => {
        contactListFieldsMapRef.current.set(contactListId, contactFieldData);
        fetchedContactListIdsRef.current.add(contactListId);
    }

    const syncContactVariablesToNodeState = (listIds: number[]) => {
        const fieldsToAdd: campaignContactFieldData[] = [];
        const seenFieldNames = new Set(syncedVariableFieldNamesRef.current);

        listIds.forEach((listId) => {
            const fields = contactListFieldsMapRef.current.get(listId) ?? [];
            fields.forEach((field) => {
                if (!seenFieldNames.has(field.contact_field_name)) {
                    seenFieldNames.add(field.contact_field_name);
                    fieldsToAdd.push(field);
                }
            });
        });

        if (fieldsToAdd.length > 0) {
            fieldsToAdd.forEach((field) => syncedVariableFieldNamesRef.current.add(field.contact_field_name));

            onChangeCurrentActiveNodeConfig(nodes, currentActiveNodeConfig, stepAction.chatbot_step_action_id,
                {
                    type: "add_contact_variable",
                    contact_fields: fieldsToAdd,
                },
                {
                    updateType: "editExistingStepAction"
                }
            );
        }
    }

    const fetchContactListFields = async (listId: number) => {
        const response = await getListOfCampaignContactFieldApi(listId);
        if (!(response.status)) {
            handleError(response);
            return;
        }

        if (response.data?.[0]?.contact_field_data) {
            storeFieldsFromResponse(listId, response.data[0].contact_field_data);
        }
    }

    const fetchFieldsForContactLists = async (contactListIds: number[]) => {
        const listsToFetch = contactListIds.filter((id) => !fetchedContactListIdsRef.current.has(id));
        if (listsToFetch.length === 0) {
            return;
        }

        startRetrieveContactLoader();
        try {
            await Promise.all(listsToFetch.map((listId) => fetchContactListFields(listId)));
            syncContactVariablesToNodeState(listsToFetch);
        } finally {
            stopRetrieveContactLoader();
        }
    }

    useEffect(() => {
        const existingListIds = (actionData?.action_retrieve_contact_list_data ?? [])
            .map((list: { contact_list_id: number }) => list.contact_list_id)
            .filter((id: number) => id && id !== -1);

        if (pendingAddedListIdsRef.current.length > 0) {
            const listIdsToFetch = pendingAddedListIdsRef.current.filter((id) => existingListIds.includes(id));
            if (listIdsToFetch.length > 0) {
                pendingAddedListIdsRef.current = pendingAddedListIdsRef.current.filter((id) => !listIdsToFetch.includes(id));
                fetchFieldsForContactLists(listIdsToFetch);
            }
            return;
        }

        if (!hasFetchedExistingListsRef.current && existingListIds.length > 0) {
            hasFetchedExistingListsRef.current = true;
            fetchFieldsForContactLists(existingListIds);
        }
    }, [actionData?.action_retrieve_contact_list_data])

    const getOptionsOfContactList = (): getCampaignContactListResponse[] => {
        if(campaignContactListData){
            return [...filterOutArchivedContactLists(campaignContactListData), emptyContactListObj];
        }
        return [];
    }

    const getValueOfContactList = () => {
        if(campaignContactListData){
            return campaignContactListData?.filter((list) => actionData?.action_retrieve_contact_list_data?.find((contactList: retrieveContactActionListData) => list?.contact_list_id === contactList?.contact_list_id)) ?? []
        } 
        return [];
    }

    const onClickOfAddNewList = () => {
        navigate(`${window.location.origin}/contacts`);
        setFlagAddNewListDialog(true);
    }

    const onChangeOfContactList = (value: getCampaignContactListResponse[] | null) => {
        const previousListIds = getValueOfContactList()?.filter((list) => list.contact_list_id !== -1)?.map((list) => list.contact_list_id);
        const newListIds = (value ?? [])?.filter((list) => list.contact_list_id !== -1)?.map((list) => list.contact_list_id);

        const addedListIds = newListIds.filter((id) => !previousListIds.includes(id));
        const removedListIds = previousListIds.filter((id) => !newListIds.includes(id));
        const contactFieldNamesToRemove = removedListIds.length > 0
            ? getContactFieldNamesToRemove(removedListIds, newListIds)
            : [];

        onChangeCurrentActiveNodeConfig(nodes, currentActiveNodeConfig, stepAction.chatbot_step_action_id, 
            {
                type: "change_contact_list",
                contact_list: value ? value : [],
                ...(removedListIds.length > 0 && newListIds.length === 0 && { remove_all_contact_variables: true }),
                ...(contactFieldNamesToRemove.length > 0 && { contact_field_names_to_remove: contactFieldNamesToRemove }),
            },
            {
                updateType: "editExistingStepAction"
            }
        );

        if (removedListIds.length > 0) {
            removedListIds.forEach((id) => {
                fetchedContactListIdsRef.current.delete(id);
                contactListFieldsMapRef.current.delete(id);
            });
        }

        if (addedListIds.length > 0) {
            pendingAddedListIdsRef.current = addedListIds;
        }
    }

    const checkDisableSaveBtn = () => {
        if(actionData?.action_retrieve_contact_list_data?.length === 0) return true;

        return !actionData?.action_retrieve_contact_variable_data?.some((variable: retrieveContactActionVariableData) => variable?.chatbot_custom_variable_configuration?.chatbot_custom_variable_configuration_id)
    }

    const shouldShowContactVariableConfig = () => {
        return getValueOfContactList()?.length > 0 && actionData?.action_retrieve_contact_variable_data?.length > 0;
    }

    const onClickOfSaveBtn = () => {
        setFlagRetrieveContactPopup(false);
        onBlurOfFallBackOfBtnVar(nodes, currentActiveNodeConfig, stepAction.chatbot_step_action_id,
            {
                ...actionData,
            },
            {
                updateType: "editExistingStepAction"
            }
        );
    }

    const renderContactListOption = (props: React.HTMLAttributes<HTMLLIElement>, option: getCampaignContactListResponse) => {
        return (
            <RenderCustomOption
                autocompleteRenderOptionProps={props}
                option={option}
                customOptionName={"New List"}
                optionName={option['contact_list_name']}
                optionLabelKey={'contact_list_id'}
                onClickOfCustomOption={() => onClickOfAddNewList()}
            />
        );
    }

    return (
        <>
            <Grid xs={12}>
                <Grid container direction={"column"} justifyContent={"center"}>
                    <Grid item textAlign={"center"}>
                        <Typography className={popupStyles.popupTitleTypography}>
                            Retrieve Contact
                        </Typography>
                    </Grid>

                    <Divider className={popupStyles.popupTitleDivider} />

                    <Grid item className={globalStyles.fullwidth}>
                        <Grid container className={popupStyles.popupBodyGridContainer} gap={2}>

                            <Grid item xs={12}>
                                <Grid container gap={"12px"}>
                                    <Grid item xs={12}>
                                        <Typography className={popupStyles.popupFieldTypography}>
                                            Contact List <Required />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid container flexWrap={"nowrap"} alignItems={'center'} gap={1}>
                                            <Grid item xs={11.5}>
                                                <Autocomplete
                                                    className={`${workflowStyles.resetVariableAutoComplete} ${globalStyles.fullwidth}`}
                                                    multiple
                                                    options={getOptionsOfContactList()}
                                                    getOptionLabel={(option) => option?.contact_list_name || ""}
                                                    filterOptions={(options, state) => filterAutocompleteOptionsWithException(options, state, "contact_list_name", "contact_list_id")}
                                                    value={getValueOfContactList()}
                                                    onChange={(_: React.SyntheticEvent, value: getCampaignContactListResponse[] | null) => onChangeOfContactList(value)}
                                                    disabled={isCurrentViewVersionPublished(currentActiveStaticWorkflow)}
                                                    ListboxProps={{ className: contactStyles.singleContactListbox }}
                                                    renderOption={renderContactListOption}
                                                    renderInput={params => {
                                                        const { InputProps, ...restParams } = params;
                                                        const { startAdornment, ...restInputProps } = InputProps;
                                                        return (
                                                            <TextField
                                                                {...restParams}
                                                                placeholder='Select Contact Lists'
                                                                InputProps={{
                                                                    ...restInputProps,
                                                                    startAdornment: (
                                                                        <div style={{
                                                                            maxHeight: "79px",
                                                                            overflowY: 'auto',
                                                                        }}
                                                                        >
                                                                            {startAdornment}
                                                                        </div>
                                                                    ),
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={0.5}>
                                                <GlobalTooltip
                                                    title="Fetch latest contact list"
                                                    placement='top'
                                                    arrow
                                                >
                                                    <RefreshIcon className={styles.refreshContactListIcon} onClick={() => getContactListApi()} />
                                                </GlobalTooltip>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            
                            {shouldShowContactVariableConfig() && 
                                <Grid item xs={12}>
                                    <RetrieveContactVariableConfig
                                        actionData={actionData}
                                        stepAction={stepAction}
                                    />  
                                </Grid>
                            }

                        </Grid>
                    </Grid>

                    <Grid item>
                        <Grid container justifyContent={"center"} alignItems={"center"} gap={"12px"} sx={{ padding: "20px 0" }}>
                            <Grid item>
                                <GlobalButton
                                    buttonText="Cancel"
                                    sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                    className='borderedButtonStyle'
                                    disabled={isCurrentViewVersionPublished(currentActiveStaticWorkflow)}
                                    onClick={() => handleCancelClick()}
                                />
                            </Grid>
                            <Grid item>
                                <GlobalButton
                                    buttonText="Save"
                                    sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                    className='primaryButtonStyle'
                                    disabled={checkDisableSaveBtn() || isCurrentViewVersionPublished(currentActiveStaticWorkflow)}
                                    onClick={() => onClickOfSaveBtn()}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default RetrieveContactPopup
